import * as THREE from "three";
import { FullScreenQuad, Pass } from "three/examples/jsm/postprocessing/Pass.js";

// The high tier's screen-space light shafts (decision 0034: "a screen-space pass only on the high tier"): the
// Wildbrush recipe's depth-only march. Each pixel takes `samples` steps toward the sun's place on screen; a step that
// lands on sky (depth at the far plane) adds e^(−9r) (r: its distance from the sun on screen), weighted by a decay
// per step, and the sum, in the sun's colour, is added to the scene. Bright walls never emit (the mask is the depth,
// not the colour). The strength fades as the sun leaves the screen or goes behind the camera. It reads the scene's
// depth: the look's post target needs `post.depth` (`createLowPolyLook`). One full-screen pass, one program.

const FRAG = /* glsl */ `
  uniform sampler2D tColor; uniform sampler2D tDepth; uniform vec2 uSun; uniform float uStrength; uniform vec3 uColor;
  uniform float uAspect; uniform float uDecay;
  varying vec2 vUv;
  float ign(vec2 p) { return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715)))); }
  void main() {
    vec4 base = texture2D(tColor, vUv);
    if (uStrength <= 0.0) { gl_FragColor = base; return; }
    vec2 delta = (uSun - vUv) / float(SAMPLES);
    vec2 uv = vUv + delta * ign(gl_FragCoord.xy);
    float illum = 0.0; float decay = 1.0;
    for (int i = 0; i < SAMPLES; i++) {
      uv += delta;
      vec2 c = clamp(uv, 0.0, 1.0);
      float sky = step(0.99999, texture2D(tDepth, c).x);
      float r = length((c - uSun) * vec2(uAspect, 1.0));
      illum += sky * exp(-r * 9.0) * decay;
      decay *= uDecay;
    }
    gl_FragColor = vec4(base.rgb + uColor * (illum / float(SAMPLES)) * uStrength, base.a);
  }`;

/** The screen-space shaft pass. Add it through `createLowPolyLook`'s `post.passes` with `post.depth`. */
export class ShaftPass extends Pass {
  readonly material: THREE.ShaderMaterial;
  private readonly quad: FullScreenQuad;
  private readonly gain: number;
  private strengthNow = 0;
  /** Whether the pass respects cloud shadow: `applyDayLook` fades it by the shade where the eye stands (IMP-0025). */
  readonly clouds: boolean;

  /** `samples` per pixel (default 24), `gain` the game's brightness, `decay` per step (default 0.96); `clouds`
   *  (default false): fade the rays by the cloud shadow where the eye stands. */
  constructor(o: { samples?: number; gain?: number; decay?: number; clouds?: boolean } = {}) {
    super();
    this.gain = o.gain ?? 1;
    this.clouds = o.clouds ?? false;
    this.material = new THREE.ShaderMaterial({
      name: "lowpoly-shaft-pass",
      defines: { SAMPLES: o.samples ?? 24 },
      uniforms: {
        tColor: { value: null },
        tDepth: { value: null },
        uSun: { value: new THREE.Vector2(0.5, 0.5) },
        uStrength: { value: 0 },
        uColor: { value: new THREE.Color() },
        uAspect: { value: 1 },
        uDecay: { value: o.decay ?? 0.96 },
      },
      vertexShader: /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false,
    });
    this.quad = new FullScreenQuad(this.material);
  }

  /** Aim at the sun (a world direction toward it) through `camera`; `strength` 0 to 1 from the look; `colour` sRGB. */
  setSun(camera: THREE.Camera, dir: readonly [number, number, number], strength: number, colour: readonly [number, number, number]): void {
    const u = this.material.uniforms;
    const far = new THREE.Vector3(dir[0], dir[1], dir[2]).multiplyScalar(1000).add(camera.getWorldPosition(new THREE.Vector3()));
    const clip = new THREE.Vector4(far.x, far.y, far.z, 1).applyMatrix4(camera.matrixWorldInverse).applyMatrix4(camera.projectionMatrix);
    let s = strength;
    if (clip.w <= 0) s = 0;
    else {
      const x = (clip.x / clip.w) * 0.5 + 0.5;
      const y = (clip.y / clip.w) * 0.5 + 0.5;
      u.uSun!.value.set(x, y);
      // fade out as the sun leaves the screen (fully gone half a screen past the edge)
      const out = Math.max(0, -x, x - 1, -y, y - 1);
      s *= Math.max(0, 1 - out / 0.5);
    }
    this.strengthNow = Math.max(0, s);
    u.uStrength!.value = this.strengthNow * this.gain;
    u.uColor!.value.setRGB(colour[0], colour[1], colour[2], THREE.SRGBColorSpace);
  }

  /** The strength last set (0 when the sun is off screen or down). */
  strength(): number {
    return this.strengthNow;
  }

  override setSize(width: number, height: number): void {
    this.material.uniforms.uAspect!.value = height > 0 ? width / height : 1;
  }

  override render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget): void {
    const u = this.material.uniforms;
    u.tColor!.value = readBuffer.texture;
    u.tDepth!.value = readBuffer.depthTexture;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    this.quad.render(renderer);
  }

  override dispose(): void {
    this.material.dispose();
    this.quad.dispose();
  }
}
